import type {
    VisibilityContextValue,
    ScrollPosition,
    ViewportDimensions,
    UseTrackVisibilityOptions,
    VisibilityCallbackData,
    TrackingConfig,
} from './types';
import type { NativeSyntheticEvent, NativeScrollEvent, View } from 'react-native';

import { calculateVisibility } from './utils';

import { log as logRNComponentSeen } from '@indeed/signals-schema/com/indeed/signals/schema/reactnative/RNComponentSeen';
import React, {
    createContext,
    useContext,
    useRef,
    useCallback,
    useEffect,
    useState,
    type RefObject,
} from 'react';

interface NormalizedTrackingOptions extends UseTrackVisibilityOptions {
    once: boolean; // Always normalized to boolean (defaults to true)
}

interface ComponentTrackingData {
    ref: RefObject<View>;
    options: NormalizedTrackingOptions;
    lastVisibilityState: boolean;
    hasTriggeredOnce: boolean;
    trackingConfig?: TrackingConfig;
}

const VisibilityTrackerContext = createContext<VisibilityContextValue | null>(null);

interface VisibilityTrackerProviderProps {
    children: React.ReactNode;
    /**
     * Viewport dimensions of the scrollable container.
     * For full-screen: use useWindowDimensions()
     * For modals/bottomsheets: measure the container dimensions
     * Can be updated dynamically via useScrollHandler().onLayout
     */
    viewportDimensions: ViewportDimensions;
    /** Debounce delay for scroll events in milliseconds */
    scrollDebounceDelay?: number;
    /** Initial scroll position */
    initialScrollPosition?: ScrollPosition;
}

export function VisibilityTrackerProvider({
    children,
    viewportDimensions: initialViewportDimensions,
    scrollDebounceDelay = 16,
    initialScrollPosition = { x: 0, y: 0 },
}: VisibilityTrackerProviderProps) {
    const [scrollPosition, setScrollPosition] = useState<ScrollPosition>(initialScrollPosition);
    const [viewportDimensions, setViewportDimensions] =
        useState<ViewportDimensions>(initialViewportDimensions);

    const componentsRef = useRef<Map<string, ComponentTrackingData>>(new Map());

    const checkVisibilityForComponent = useCallback(
        (id: string) => {
            const componentData = componentsRef.current.get(id);
            if (!componentData) {
                return;
            }

            const { ref, options, hasTriggeredOnce } = componentData;

            // Skip if disabled
            if (options.enabled === false) {
                return;
            }

            // Skip if already triggered once and once option is enabled (default behavior)
            if (options.once && hasTriggeredOnce) {
                return;
            }

            if (!ref.current) {
                return;
            }

            // Measure the component layout
            ref.current.measure((x: number, y: number, width: number, height: number) => {
                // CRITICAL: Get fresh data from Map to avoid stale closure
                const currentComponentData = componentsRef.current.get(id);
                if (!currentComponentData) {
                    return; // Component was unregistered
                }

                const layout = { x, y, width, height };

                const { isVisible, visibilityRatio, intersectionRect } = calculateVisibility(
                    layout,
                    scrollPosition,
                    viewportDimensions,
                    currentComponentData.options.threshold,
                );

                const callbackData: VisibilityCallbackData = {
                    isVisible,
                    visibilityRatio,
                    entry: {
                        boundingClientRect: layout,
                        intersectionRect,
                        target: ref,
                    },
                };

                // Update visibility state
                if (isVisible !== currentComponentData.lastVisibilityState) {
                    currentComponentData.lastVisibilityState = isVisible;

                    // Log component seen if trackingConfig is provided and this is the first trigger
                    if (
                        isVisible &&
                        !currentComponentData.hasTriggeredOnce &&
                        currentComponentData.trackingConfig
                    ) {
                        logRNComponentSeen({
                            provider: currentComponentData.trackingConfig.provider,
                            trackingKey: currentComponentData.trackingConfig.trackingKey,
                            componentName: currentComponentData.trackingConfig.componentName,
                            width: { long: layout.width },
                            height: { long: layout.height },
                        });
                    }

                    currentComponentData.hasTriggeredOnce = true;

                    // Call appropriate callbacks
                    if (isVisible) {
                        currentComponentData.options.onBecomeVisible?.(callbackData);
                    } else {
                        currentComponentData.options.onBecomeInvisible?.(callbackData);
                    }

                    currentComponentData.options.onVisibilityChange?.(callbackData);
                }
            });
        },
        [scrollPosition, viewportDimensions],
    );

    const checkAllVisibility = useCallback(() => {
        componentsRef.current.forEach((_, id) => {
            checkVisibilityForComponent(id);
        });
    }, [checkVisibilityForComponent]);

    const debounceTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    const triggerVisibilityCheck = useCallback(() => {
        // Clear previous timeout if exists
        if (debounceTimeoutRef.current) {
            clearTimeout(debounceTimeoutRef.current);
        }

        debounceTimeoutRef.current = setTimeout(() => {
            checkAllVisibility();
        }, scrollDebounceDelay);
    }, [checkAllVisibility, scrollDebounceDelay]);

    const registerComponent = useCallback(
        (id: string, ref: RefObject<View>, options: UseTrackVisibilityOptions) => {
            componentsRef.current.set(id, {
                ref,
                options: {
                    ...options,
                    once: options.once ?? true,
                },
                lastVisibilityState: false,
                hasTriggeredOnce: false,
                trackingConfig: options.trackingConfig,
            });

            triggerVisibilityCheck();
        },
        [triggerVisibilityCheck],
    );

    const unregisterComponent = useCallback((id: string) => {
        componentsRef.current.delete(id);
    }, []);

    const checkVisibility = useCallback(
        (id: string) => {
            checkVisibilityForComponent(id);
        },
        [checkVisibilityForComponent],
    );

    // Check visibility when scroll position or viewport changes
    useEffect(() => {
        triggerVisibilityCheck();
    }, [scrollPosition, viewportDimensions, triggerVisibilityCheck]);

    useEffect(() => {
        return () => {
            if (debounceTimeoutRef.current) {
                clearTimeout(debounceTimeoutRef.current);
            }
        };
    }, []);

    const contextValue: VisibilityContextValue = {
        scrollPosition,
        viewportDimensions,
        registerComponent,
        unregisterComponent,
        checkVisibility,
        updateScrollPosition: setScrollPosition,
        updateViewportDimensions: setViewportDimensions,
    };

    return (
        <VisibilityTrackerContext.Provider value={contextValue}>
            {children}
        </VisibilityTrackerContext.Provider>
    );
}

/**
 * Hook that provides scroll and layout handlers for integrating with existing ScrollViews
 * @returns Object with scroll and layout event handlers
 */
export function useScrollHandler() {
    const context = useVisibilityTrackerContext();

    const handleScroll = useCallback(
        (event: NativeSyntheticEvent<NativeScrollEvent>) => {
            const { contentOffset } = event.nativeEvent;
            context.updateScrollPosition({ x: contentOffset.x, y: contentOffset.y });
        },
        [context],
    );

    const handleLayout = useCallback(
        (event: any) => {
            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
            const { width, height } = event.nativeEvent.layout;

            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
            context.updateViewportDimensions({ width, height });
        },
        [context],
    );

    return {
        onScroll: handleScroll,
        onLayout: handleLayout,
        scrollEventThrottle: 16,
    };
}

export function useVisibilityTrackerContext(): VisibilityContextValue {
    const context = useContext(VisibilityTrackerContext);
    if (!context) {
        throw new Error(
            'useVisibilityTrackerContext must be used within a VisibilityTrackerProvider',
        );
    }
    return context;
}

/**
 * Hook for manually updating scroll position (useful for integration with existing scroll systems)
 */
export function useUpdateScrollPosition() {
    const context = useVisibilityTrackerContext();
    return context.updateScrollPosition;
}

/**
 * Hook for manually updating viewport dimensions
 */
export function useUpdateViewportDimensions() {
    const context = useVisibilityTrackerContext();
    return context.updateViewportDimensions;
}
